import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloseIcon, ChevronLeft, ChevronRight } from './Icons';
import useSwipe from '../hooks/useSwipe';
import ResponsiveImage from './ResponsiveImage';

const ServiceModal = ({ service, onClose }) => {
    const [imageIndex, setImageIndex] = useState(0);

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    const nextImage = () => {
        setImageIndex((prev) => (prev + 1) % service.images.length);
    };

    const prevImage = () => {
        setImageIndex((prev) => (prev - 1 + service.images.length) % service.images.length);
    };

    // Swipe handlers for mobile gallery navigation
    const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(
        nextImage,  // swipe left = next image
        prevImage   // swipe right = previous image
    );

    const handleContactClick = () => {
        onClose();
        setTimeout(() => {
            const element = document.getElementById('contact');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }, 300);
    };

    return (
        <div
            className="project-modal-overlay active"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="project-modal">
                <button
                    className="project-modal-close"
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    <CloseIcon />
                </button>

                {/* Gallery */}
                <div
                    className="project-modal-gallery"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    <div
                        className="project-modal-gallery-track"
                        style={{ transform: `translateX(-${imageIndex * 100}%)` }}
                    >
                        {service.images.map((img, idx) => (
                            <div className="project-modal-gallery-slide" key={idx}>
                                <ResponsiveImage
                                    src={img}
                                    alt={`${service.title} - ${service.category} - Image ${idx + 1}`}
                                />
                            </div>
                        ))}
                    </div>

                    {service.images.length > 1 && (
                        <>
                            <button
                                className="project-modal-gallery-nav prev"
                                onClick={prevImage}
                                aria-label="Previous image"
                            >
                                <ChevronLeft />
                            </button>
                            <button
                                className="project-modal-gallery-nav next"
                                onClick={nextImage}
                                aria-label="Next image"
                            >
                                <ChevronRight />
                            </button>
                            <div className="project-modal-gallery-dots">
                                {service.images.map((_, idx) => (
                                    <button
                                        key={idx}
                                        className={`project-modal-gallery-dot ${idx === imageIndex ? 'active' : ''}`}
                                        onClick={() => setImageIndex(idx)}
                                        aria-label={`Go to image ${idx + 1}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Content */}
                <div className="project-modal-content">
                    <span className="project-modal-category">{service.category}</span>
                    <h2 className="project-modal-title">{service.title}</h2>

                    {Array.isArray(service.fullDescription) ?
                        service.fullDescription.map((paragraph, idx) => (
                            <p key={idx} className="project-modal-description">{paragraph}</p>
                        ))
                        :
                        <p className="project-modal-description">{service.fullDescription}</p>
                    }

                    <div className="project-modal-details">
                        <h4 className="project-modal-details-title">What We Offer</h4>
                        <ul className="project-modal-details-list">
                            {service.offerings.map((offering, idx) => (
                                <li key={idx}>{offering}</li>
                            ))}
                        </ul>
                    </div>

                    <div className="project-modal-cta">
                        <p>Ready to discuss your project?</p>
                        <div className="project-modal-cta-buttons">
                            <button
                                className="btn-primary-custom"
                                onClick={handleContactClick}
                            >
                                Request a Consultation
                            </button>
                            <Link
                                to={`/services/${service.id}`}
                                className="btn-secondary-custom"
                                onClick={onClose}
                            >
                                View Full Page
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceModal;
